import { Budget } from "../Budget";
import { BudgetLabels } from "../budget.labels";
import { FormatService } from "../../../../services/format.service";
import { Table } from "../../../s25-table/Table";
import { S25RouterLinkComponent } from "../../../s25-nav-bar/s25.router.link.component";
import { S25States } from "../../../../services/state/state.map";
import { S25Assert } from "../../../../util/s25.assert";
import {
    ReportMeasureOption,
    ReportSummaryCard,
    ReportWidgetDef,
} from "../../../../standalone/report-dashboard/report.types";

export const BUDGET_REPORTS_DASHBOARD_ID = "budgetReports";

export interface BudgetReportWidgetDef extends ReportWidgetDef {
    dimension?: Budget.ReportDimension;
}

export const BUDGET_REPORT_WIDGETS: BudgetReportWidgetDef[] = [
    { key: "summary", title: "Summary", chartType: "cards" },
    { key: "orgType", title: "Organization Type", chartType: "horizontalBar", dimension: "ORG_TYPE" },
    { key: "orgStanding", title: "Organization Standing", chartType: "pie", dimension: "ORG_STANDING" },
    { key: "orgCategory", title: "Organization Category", chartType: "horizontalBar", dimension: "ORG_CATEGORY" },
    { key: "percentPlanned", title: "Percent Planned", chartType: "bar", dimension: "PERCENT_PLANNED" },
    { key: "planType", title: `${BudgetLabels.BUDGET} Type`, chartType: "pie", dimension: "PLAN_TYPE" },
    {
        key: "lineItemCategory",
        title: "Line Item Category",
        chartType: "horizontalBar",
        dimension: "LINE_ITEM_CATEGORY",
    },
];

export const BUDGET_REPORT_MEASURES: ReportMeasureOption<Budget.ReportMeasure>[] = [
    { key: "allocated", label: "Allocated" },
    { key: "planned", label: "Planned" },
    { key: "spent", label: "Spent" },
    { key: "committed", label: "Committed" },
];

export const DEFAULT_BUDGET_REPORT_MEASURE: Budget.ReportMeasure = "allocated";

export function isBudgetReportMeasure(value: unknown): value is Budget.ReportMeasure {
    return BUDGET_REPORT_MEASURES.some((measure) => measure.key === value);
}

/** Server validation errors are returned as HTTP 200 with {@link Budget.ReportValidationFields}. */
export function budgetReportValidationMessage(
    response: Budget.ReportValidationFields | null | undefined,
): string | undefined {
    const message = response?.message?.trim();
    return message || undefined;
}

export function formatBudgetAmount(value: number): string {
    return FormatService.formatCurrency(value ?? 0, "en-US", "$", "USD", "1.0-0");
}

/** Plan-type rows arrive with the raw stored code as the label; show the display name instead. */
export function withPlanTypeDisplayLabels(breakdown: Budget.ReportBreakdownDoc): Budget.ReportBreakdownDoc {
    if (breakdown.dimension !== "PLAN_TYPE") {
        return breakdown;
    }
    return {
        ...breakdown,
        rows: breakdown.rows.map((row) =>
            row.key === Budget.REPORT_NONE_KEY
                ? row
                : { ...row, label: Budget.budgetPlanTypeDisplayName(row.label) ?? row.label },
        ),
    };
}

export function budgetSummaryCards(summary: Budget.ReportSummary | undefined): ReportSummaryCard[] {
    if (!summary) {
        return [];
    }
    return [
        { label: "Allocated", value: formatBudgetAmount(summary.totalAllocated) },
        { label: "Planned", value: formatBudgetAmount(summary.totalPlanned) },
        { label: "Spent", value: formatBudgetAmount(summary.totalSpent) },
        { label: "Committed", value: formatBudgetAmount(summary.totalCommitted) },
        { label: BudgetLabels.ALLOCATIONS, value: String(summary.orgBudgetCount ?? 0) },
        { label: BudgetLabels.BUDGETS, value: String(summary.planCount ?? 0) },
        { label: "Line Items", value: String(summary.lineItemCount ?? 0) },
    ];
}

type DrillDownColumnId =
    | "organizationName"
    | "budgetName"
    | "planName"
    | "planType"
    | "lineItemName"
    | "categoryName"
    | "fiscalYearName"
    | "percentPlanned"
    | Budget.ReportMeasure;

const DRILL_DOWN_COLUMN_HEADERS: Record<DrillDownColumnId, string> = {
    organizationName: "Organization",
    budgetName: BudgetLabels.ALLOCATION,
    planName: BudgetLabels.BUDGET,
    planType: `${BudgetLabels.BUDGET} Type`,
    lineItemName: "Line Item",
    categoryName: BudgetLabels.BUDGET_CATEGORY,
    fiscalYearName: "Fiscal Year",
    percentPlanned: "Percent Planned",
    allocated: "Allocated",
    planned: "Planned",
    spent: "Spent",
    committed: "Committed",
};

const DRILL_DOWN_COLUMNS_BY_ENTITY: Record<Budget.ReportEntityType, DrillDownColumnId[]> = {
    ORG_BUDGET: [
        "organizationName",
        "budgetName",
        "fiscalYearName",
        "percentPlanned",
        "allocated",
        "planned",
        "spent",
        "committed",
    ],
    PLAN: ["organizationName", "budgetName", "planName", "planType", "allocated", "spent", "committed"],
    LINE_ITEM: ["organizationName", "planName", "lineItemName", "categoryName", "planned", "spent", "committed"],
};

const AMOUNT_COLUMN_IDS = new Set<DrillDownColumnId>(BUDGET_REPORT_MEASURES.map((measure) => measure.key));

export function drillDownColumns(entityType: Budget.ReportEntityType): Table.Column[] {
    return DRILL_DOWN_COLUMNS_BY_ENTITY[entityType].map((columnId) => ({
        id: columnId,
        header: DRILL_DOWN_COLUMN_HEADERS[columnId],
        align: AMOUNT_COLUMN_IDS.has(columnId) || columnId === "percentPlanned" ? "right" : "left",
    }));
}

function amountCell(value: number | undefined): Table.Cell {
    const amount = value ?? 0;
    return { text: formatBudgetAmount(amount), sortValue: String(amount) };
}

function percentCell(value: number | undefined): Table.Cell {
    if (value === undefined || value === null) {
        return { text: "" };
    }
    return { text: `${value.toFixed(1)}%`, sortValue: String(value) };
}

function drillDownRowId(row: Budget.ReportDrilldownRow, entityType: Budget.ReportEntityType, rowIndex: number): string {
    switch (entityType) {
        case "ORG_BUDGET":
            return `${row.seriesKey}:${row.budgetId}`;
        case "PLAN":
            return `${row.seriesKey}:${row.budgetPlanId ?? rowIndex}`;
        case "LINE_ITEM":
            return `${row.seriesKey}:${row.lineItemId ?? rowIndex}`;
        default:
            return S25Assert.never(entityType, "Unknown budget report entity type");
    }
}

export function drillDownTableRows(response: Budget.ReportDrilldownResponse): Table.Row[] {
    return (response.rows ?? []).map((row, rowIndex) => ({
        id: drillDownRowId(row, response.entityType, rowIndex),
        name: row.lineItemName ?? row.planName ?? row.budgetName ?? row.organizationName,
        cells: {
            organizationName: {
                component: S25RouterLinkComponent,
                inputs: {
                    text: row.organizationName ?? "",
                    stateName: S25States.names.seOrganizationDetails,
                    stateParams: { itemId: row.organizationId },
                },
                textValue: row.organizationName ?? "",
            },
            budgetName: { text: row.budgetName ?? "" },
            planName: { text: row.planName ?? "" },
            planType: { text: Budget.budgetPlanTypeDisplayName(row.planType) ?? "" },
            lineItemName: { text: row.lineItemName ?? "" },
            categoryName: { text: row.categoryName ?? "" },
            fiscalYearName: { text: row.fiscalYearName ?? "" },
            percentPlanned: percentCell(row.percentPlanned),
            allocated: amountCell(row.measures?.allocated),
            planned: amountCell(row.measures?.planned),
            spent: amountCell(row.measures?.spent),
            committed: amountCell(row.measures?.committed),
        },
    }));
}
